import '@formatjs/intl-getcanonicallocales/polyfill';
import { AppVersion } from '@hub/layout/AppVersion/AppVersion';
import type { SSRConfig } from 'next-i18next';
import { appWithTranslation, i18n } from 'next-i18next';
import '../styles/global.css';
import '../styles/tippy-tooltip.css';
import { Toasts } from '@hub/hooks/useToasts';
import { useSessionStoreInitializer } from '@hub/hooks/useSessionStoreInitializer';
import Head from 'next/head';
import { PiwikPro } from '@hub/layout/PiwikPro/PiwikPro';
import { useRouter } from 'next/router';
import { GoogleTagManager } from '@hub/layout/GoogleTagManager/GoogleTagManager';
import { getLocale } from '@hub/libs/constants';
import getConfig from 'next/config';
import type { AppProps } from 'next/app';
import type { ComponentType } from 'react';
import { useState } from 'react';
import type { NextRuntimeConfiguration } from '@hub/libs/config';
import type { Translations } from '@icommerce/i18n';
import { isGtmEnabledForLocale, isJentisEnabledForLocale } from '@icommerce/i18n';
import { getPiwikId } from '@icommerce/i18n/src';
import { CountrySwitchContextProvider, NavigationProvider } from '@cms/ui';
import { HydrationBoundary, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { clientSideNextI18nConfig } from '@hub/libs/i18n.helper';
import { Jentis } from '@hub/layout/Jentis/Jentis';

const { publicRuntimeConfig } = getConfig() as NextRuntimeConfiguration;

const MyApp = ({ Component, pageProps }: AppProps) => {
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            retry: 1,
            staleTime: Infinity,
            gcTime: publicRuntimeConfig.useQueryCacheTtl,
          },
          mutations: {
            retry: 0,
          },
        },
      })
  );
  const router = useRouter();
  const locale = getLocale(router);
  const isGtmEnabled = isGtmEnabledForLocale(locale);
  const isJentisEnabled = isJentisEnabledForLocale(locale);
  useSessionStoreInitializer();

  // eslint-disable-next-line no-underscore-dangle
  const nextI18Next = (pageProps as SSRConfig)._nextI18Next;

  if (nextI18Next?.userConfig?.resources) {
    // Add resources to the i18n initial store for SSR
    const resources = nextI18Next.userConfig.resources as Translations;
    for (const i18NLocale in resources) {
      if (Object.hasOwn(resources, i18NLocale)) {
        const localResource = resources[i18NLocale] as Translations;
        for (const namespace in localResource) {
          if (Object.hasOwn(localResource, namespace)) {
            i18n.addResourceBundle(i18NLocale, namespace, localResource[namespace]);
          }
        }
      }
    }
  }

  return (
    <>
      <Head>
        <meta name="viewport" content="initial-scale=1.0, width=device-width" />
      </Head>
      {getPiwikId(publicRuntimeConfig.envName, locale) && (
        <PiwikPro containerId={getPiwikId(publicRuntimeConfig.envName, locale)} />
      )}
      {isGtmEnabled && <GoogleTagManager id={publicRuntimeConfig.googleTagManagerId} />}
      {isJentisEnabled && publicRuntimeConfig.jentisUrl && <Jentis url={publicRuntimeConfig.jentisUrl} />}
      <QueryClientProvider client={queryClient}>
        <AppVersion />
        {/* eslint-disable-next-line @typescript-eslint/no-unsafe-assignment,@typescript-eslint/no-unsafe-member-access */}
        <HydrationBoundary state={pageProps.dehydratedState}>
          <CountrySwitchContextProvider>
            <NavigationProvider>
              {/* eslint-disable-next-line react/jsx-props-no-spreading */}
              <Component {...pageProps} />
            </NavigationProvider>
          </CountrySwitchContextProvider>
        </HydrationBoundary>
        <Toasts />
      </QueryClientProvider>
    </>
  );
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export default appWithTranslation(MyApp as ComponentType<AppProps<any>>, clientSideNextI18nConfig);
